/**
 * Quiz-Specific Stats Component
 *
 * Shows progress and improvement for each quiz.
 * Focuses on "then vs now" comparisons with encouraging language.
 *
 * Philosophy: Celebrate improvement, not just accuracy.
 */

import { useMemo, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/db/database";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Clock, Target, Calendar, ChevronRight, Play, ChevronDown } from "lucide-react";
import { getQuizFlag } from "@/utils/quizMetadata";
import { getDifficultyBadgeLabel, getDifficultyBadgeVariant } from "@/utils/difficultyCalculator";
import { QuizPreviewMap } from "./QuizPreviewMap";
import { DatasetPreviewMap } from "./DatasetPreviewMap";
import { SessionDetailView } from "./SessionDetailView";
import { QuizSession } from "@/db/types";

// Helper to identify anomalous sessions (likely user went AFK)
function isAnomalousSession(session: QuizSession): boolean {
  const MAX_AVG_TIME_PER_QUESTION_MS = 60 * 1000; // 1 minute
  return session.averageTimePerQuestionMs > MAX_AVG_TIME_PER_QUESTION_MS;
}

export function QuizSpecificStats() {
  const [selectedQuizSig, setSelectedQuizSig] = useState<string | null>(null);
  const [selectedDatasetKey, setSelectedDatasetKey] = useState<string | null>(null);
  const [expandedDatasets, setExpandedDatasets] = useState<Set<string>>(new Set());
  const [selectedSession, setSelectedSession] = useState<QuizSession | null>(null);

  // Load all data
  const configs = useLiveQuery(() => db.quiz_configs.toArray());
  const sessions = useLiveQuery(() => db.quiz_sessions.toArray());
  const attempts = useLiveQuery(() => db.quiz_attempts.toArray());

  // Calculate stats per quiz
  const quizStats = useMemo(() => {
    if (!configs || !sessions || !attempts) return [];

    // Group sessions by quiz
    const sessionsByQuiz = sessions.reduce((acc, session) => {
      const sig = session.quizSignature;
      if (!acc[sig]) {
        acc[sig] = [];
      }
      acc[sig]!.push(session); // Non-null assertion: we just created it above
      return acc;
    }, {} as Record<string, typeof sessions>);

    // Calculate stats for each quiz
    return configs
      .filter((config) => (sessionsByQuiz[config.signature]?.length ?? 0) > 0)
      .map((config) => {
        const quizSessions = sessionsByQuiz[config.signature] ?? [];
        const sortedSessions = [...quizSessions].sort(
          (a, b) =>
            new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime()
        );

        const lastSession = sortedSessions[0]; // Most recent (now sorted desc)
        const firstSession = sortedSessions[sortedSessions.length - 1]; // Oldest

        // Safety check: should never happen due to filter above, but TypeScript needs it
        if (!firstSession || !lastSession) {
          return null;
        }

        // Filter out anomalous sessions (>1 min/question) from time aggregates
        const validSessions = quizSessions.filter(s => !isAnomalousSession(s));
        const anomalousCount = quizSessions.length - validSessions.length;

        const totalTime = validSessions.reduce(
          (sum, s) => sum + (s.totalTimeMs || 0),
          0
        );

        const averageAccuracy =
          quizSessions.reduce((sum, s) => sum + s.accuracyRate, 0) /
          quizSessions.length;

        const improvement =
          lastSession.accuracyRate - firstSession.accuracyRate;

        const daysSinceLastSession = Math.floor(
          (Date.now() - new Date(lastSession.completedAt).getTime()) /
            (24 * 60 * 60 * 1000)
        );

        return {
          config,
          sessionCount: quizSessions.length,
          anomalousSessionCount: anomalousCount,
          firstAccuracy: firstSession.accuracyRate,
          latestAccuracy: lastSession.accuracyRate,
          averageAccuracy,
          improvement,
          totalTimeMs: totalTime,
          lastPracticed: lastSession.completedAt,
          daysSinceLastSession,
          sessions: sortedSessions,
        };
      })
      .filter((stat) => stat !== null)
      .sort((a, b) => {
        // Sort by last practiced (most recent first)
        return (
          new Date(b.lastPracticed).getTime() -
          new Date(a.lastPracticed).getTime()
        );
      });
  }, [configs, sessions, attempts]);

  // Group quizzes by dataset
  const groupedQuizzes = useMemo(() => {
    const groups = new Map<string, typeof quizStats>();

    quizStats.forEach((stat) => {
      let groupKey: string;

      if (stat.config.datasetSource?.type === "dataset-url") {
        // Group by dataset path
        groupKey = stat.config.datasetSource.path;
      } else if (stat.config.datasetSource?.type === "inline") {
        // Each uploaded file is its own group
        groupKey = `inline-${stat.config.datasetSource.id}`;
      } else {
        // Fallback for other types
        groupKey = stat.config.datasetSignature;
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(stat);
    });

    // Convert to array and sort by most recent activity
    return Array.from(groups.entries()).map(([key, quizzes]) => {
      const mostRecent = Math.max(...quizzes.map(q => new Date(q.lastPracticed).getTime()));

      // Calculate aggregate stats for the dataset
      const totalSessions = quizzes.reduce((sum, q) => sum + q.sessionCount, 0);
      const totalTime = quizzes.reduce((sum, q) => sum + q.totalTimeMs, 0);
      const avgAccuracy = quizzes.reduce((sum, q) => sum + q.averageAccuracy, 0) / quizzes.length;
      const totalQuestions = quizzes.reduce((sum, q) => {
        return sum + q.sessions.reduce((s, sess) => s + sess.totalQuestions, 0);
      }, 0);

      return {
        key,
        label: quizzes[0]!.config.datasetLabel || key,
        flag: getQuizFlag(quizzes[0]!.config), // Use first quiz's flag for the dataset
        geography: quizzes[0]!.config.geography,
        quizzes,
        mostRecent,
        totalSessions,
        totalTime,
        avgAccuracy,
        totalQuestions,
      };
    }).sort((a, b) => b.mostRecent - a.mostRecent);
  }, [quizStats]);

  // Toggle dataset expansion
  const toggleDataset = (datasetKey: string) => {
    setExpandedDatasets(prev => {
      const next = new Set(prev);
      if (next.has(datasetKey)) {
        next.delete(datasetKey);
      } else {
        next.add(datasetKey);
      }
      return next;
    });
  };

  // Select first quiz by default
  const selectedQuiz = useMemo(() => {
    if (!selectedQuizSig && quizStats.length > 0) {
      return quizStats[0];
    }
    return quizStats.find((q) => q.config.signature === selectedQuizSig);
  }, [selectedQuizSig, quizStats]);

  // Get selected dataset
  const selectedDataset = useMemo(() => {
    return groupedQuizzes.find((g) => g.key === selectedDatasetKey);
  }, [selectedDatasetKey, groupedQuizzes]);

  if (!configs || !sessions) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">Loading quiz stats...</p>
        </CardContent>
      </Card>
    );
  }

  if (quizStats.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Quiz Activity Yet</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Complete your first quiz to see detailed progress tracking!
          </p>
          <Button onClick={() => (window.location.href = "/quiz/studio")}>
            Create a Quiz
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Quiz List */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle className="text-lg">Your Quizzes</CardTitle>
          <p className="text-sm text-muted-foreground">
            {quizStats.length} {quizStats.length === 1 ? "quiz" : "quizzes"} in{" "}
            {groupedQuizzes.length} {groupedQuizzes.length === 1 ? "dataset" : "datasets"}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {groupedQuizzes.map((group) => {
            const isExpanded = expandedDatasets.has(group.key);
            const isSelected = selectedDatasetKey === group.key;

            return (
              <div key={group.key} className="space-y-2">
                {/* Dataset Header */}
                <div
                  className={`p-3 rounded-lg border transition-colors ${
                    isSelected
                      ? "bg-primary/10 border-primary"
                      : "hover:bg-muted border-border"
                  }`}
                >
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => {
                      setSelectedDatasetKey(group.key);
                      setSelectedQuizSig(null);
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate">
                        <span className="mr-2">{group.flag}</span>
                        {group.label}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <span>{group.quizzes.length} {group.quizzes.length === 1 ? "quiz" : "quizzes"}</span>
                        <span>•</span>
                        <span>{group.totalSessions} sessions</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDataset(group.key);
                      }}
                      className="flex-shrink-0 ml-2 p-1 hover:bg-background/50 rounded transition-colors"
                    >
                      <ChevronDown
                        className={`w-4 h-4 text-muted-foreground transition-transform ${
                          isExpanded ? "transform rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Quiz List (collapsed by default) */}
                {isExpanded && (
                  <div className="space-y-2 pl-3">
                    {group.quizzes.map((stat) => {
                      // Get the quiz size from most recent session (number of questions in the quiz)
                      const quizSize = stat.sessions[0]?.totalQuestions || 0;

                      return (
                        <button
                          key={stat.config.signature}
                          onClick={() => {
                            setSelectedQuizSig(stat.config.signature);
                            setSelectedDatasetKey(null);
                          }}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            selectedQuiz?.config.signature === stat.config.signature
                              ? "bg-primary/10 border-primary"
                              : "hover:bg-muted border-transparent"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium truncate text-sm">
                                <span className="mr-2">{getQuizFlag(stat.config)}</span>
                                {stat.config.title}
                              </h4>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                <span>{stat.sessionCount} sessions</span>
                                <span>•</span>
                                <span>
                                  {stat.daysSinceLastSession === 0
                                    ? "Today"
                                    : `${stat.daysSinceLastSession}d ago`}
                                </span>
                                <span>•</span>
                                <span>{quizSize} questions</span>
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 ml-2" />
                          </div>

                          {/* Mini improvement indicator */}
                          {stat.improvement !== 0 && (
                            <div
                              className={`text-xs mt-2 font-medium ${
                                stat.improvement > 0
                                  ? "text-green-600 dark:text-green-400"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {stat.improvement > 0 ? "↑" : ""}
                              {Math.abs(stat.improvement).toFixed(0)}% improvement
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Selected Dataset Details */}
      {selectedDataset && (
        <div className="lg:col-span-2 space-y-6">
          {/* Dataset Info Header */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <CardTitle>
                    <span className="mr-2">{selectedDataset.flag}</span>
                    {selectedDataset.label}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-2">
                    {selectedDataset.quizzes.length} {selectedDataset.quizzes.length === 1 ? "quiz" : "quizzes"} • {selectedDataset.geography}
                  </p>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Dataset Preview Map - show full unfiltered dataset */}
          {selectedDataset.quizzes[0]!.config.datasetSource?.type === "dataset-url" ? (
            <DatasetPreviewMap
              datasetPath={selectedDataset.quizzes[0]!.config.datasetSource.path}
              height="280px"
            />
          ) : (
            <QuizPreviewMap config={selectedDataset.quizzes[0]!.config} height="280px" />
          )}

          {/* Dataset Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2">
            <StatCard
              title="Total Sessions"
              value={selectedDataset.totalSessions}
              subtitle="Across all quizzes"
              icon={<Target className="w-4 h-4" />}
            />

            <StatCard
              title="Time Invested"
              value={formatTime(selectedDataset.totalTime)}
              subtitle="Learning this dataset"
              icon={<Clock className="w-4 h-4" />}
            />

            <StatCard
              title="Average Accuracy"
              value={`${selectedDataset.avgAccuracy.toFixed(0)}%`}
              subtitle="Across all quizzes"
              icon={<TrendingUp className="w-4 h-4" />}
            />

            <StatCard
              title="Questions Answered"
              value={selectedDataset.totalQuestions}
              subtitle="Total practice questions"
              icon={<Calendar className="w-4 h-4" />}
            />
          </div>

          {/* Quiz Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quiz Breakdown</CardTitle>
              <p className="text-sm text-muted-foreground">
                Individual quizzes in this dataset
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {selectedDataset.quizzes.map((quiz) => {
                  // Get the quiz size from most recent session
                  const quizSize = quiz.sessions[0]?.totalQuestions || 0;

                  return (
                    <button
                      key={quiz.config.signature}
                      onClick={() => {
                        setSelectedQuizSig(quiz.config.signature);
                        setSelectedDatasetKey(null);
                      }}
                      className="w-full text-left p-4 rounded-lg border hover:bg-muted border-border transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate">
                            <span className="mr-2">{getQuizFlag(quiz.config)}</span>
                            {quiz.config.title}
                          </h4>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-2">
                            <span>{quiz.sessionCount} sessions</span>
                            <span>•</span>
                            <span>{quizSize} questions</span>
                            <span>•</span>
                            <span>{quiz.latestAccuracy.toFixed(0)}% accuracy</span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 ml-2" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Selected Quiz Details */}
      {selectedQuiz && !selectedDataset && (
        <div className="lg:col-span-2 space-y-6">
          {/* Quiz Info Header */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <CardTitle>
                    <span className="mr-2">{getQuizFlag(selectedQuiz.config)}</span>
                    {selectedQuiz.config.title}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-2">
                    {selectedQuiz.config.description || "No description"}
                  </p>
                </div>
                <Button
                  onClick={() =>
                    (window.location.href = `/quiz/play?signature=${selectedQuiz.config.signature}`)
                  }
                  className="flex-shrink-0"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Play Quiz
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Quiz Preview Map */}
          <QuizPreviewMap config={selectedQuiz.config} height="280px" />

          {/* Improvement Highlight */}
          {selectedQuiz.improvement > 0 && (
            <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="text-4xl">📈</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">
                      You're Getting Better!
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {selectedQuiz.firstAccuracy.toFixed(0)}% →{" "}
                      {selectedQuiz.latestAccuracy.toFixed(0)}% (↑
                      {selectedQuiz.improvement.toFixed(0)}%)
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {selectedQuiz.sessionCount} practice{" "}
                      {selectedQuiz.sessionCount === 1 ? "session" : "sessions"}{" "}
                      • {formatTime(selectedQuiz.totalTimeMs)} invested
                      {selectedQuiz.anomalousSessionCount > 0 && (
                        <span className="text-xs opacity-80" title={`${selectedQuiz.anomalousSessionCount} session${selectedQuiz.anomalousSessionCount === 1 ? '' : 's'} excluded (>1 min per question)`}>
                          {" "}({selectedQuiz.anomalousSessionCount} excluded)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2">
            <StatCard
              title="Practice Sessions"
              value={selectedQuiz.sessionCount}
              subtitle="Times you've practiced this"
              icon={<Target className="w-4 h-4" />}
            />

            <StatCard
              title="Time Invested"
              value={formatTime(selectedQuiz.totalTimeMs)}
              subtitle={
                selectedQuiz.anomalousSessionCount > 0
                  ? `Learning and reviewing (${selectedQuiz.anomalousSessionCount} session${selectedQuiz.anomalousSessionCount === 1 ? '' : 's'} excluded)`
                  : "Learning and reviewing"
              }
              icon={<Clock className="w-4 h-4" />}
            />

            <StatCard
              title="Latest Accuracy"
              value={`${selectedQuiz.latestAccuracy.toFixed(0)}%`}
              subtitle="Most recent session"
              icon={<TrendingUp className="w-4 h-4" />}
              trend={
                selectedQuiz.improvement > 0
                  ? `+${selectedQuiz.improvement.toFixed(0)}% from first attempt`
                  : undefined
              }
            />

            <StatCard
              title="Last Practiced"
              value={
                selectedQuiz.daysSinceLastSession === 0
                  ? "Today"
                  : `${selectedQuiz.daysSinceLastSession} ${
                      selectedQuiz.daysSinceLastSession === 1 ? "day" : "days"
                    } ago`
              }
              subtitle={new Date(
                selectedQuiz.lastPracticed
              ).toLocaleDateString()}
              icon={<Calendar className="w-4 h-4" />}
            />
          </div>

          {/* Session History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Session History</CardTitle>
              <p className="text-sm text-muted-foreground">
                Your practice journey for this quiz
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {selectedQuiz.sessions.map((session, index) => {
                    const isFirst = index === selectedQuiz.sessions.length - 1; // Oldest session
                    const isLatest = index === 0; // Most recent session

                    // Get difficulty info (Backlog #04: Track Quiz Display Mode)
                    const difficultyMultiplier = session.difficultyMultiplier || 1.0;
                    const difficultyLabel = getDifficultyBadgeLabel(difficultyMultiplier);
                    const difficultyVariant = getDifficultyBadgeVariant(difficultyMultiplier);

                    // Get active map settings
                    const mapSettings: string[] = [];
                    if (session.displaySettings) {
                      if (session.displaySettings.hideOutlines) mapSettings.push("No Borders");
                      if (session.displaySettings.disableHover) mapSettings.push("No Hover");
                      if (session.displaySettings.hideIndicatorColors) mapSettings.push("No Feedback");
                      if (session.displaySettings.hideLabels) mapSettings.push("No Labels");
                    }

                    return (
                      <button
                        key={session.id}
                        onClick={() => setSelectedSession(session)}
                        className={`w-full text-left p-4 rounded-lg border transition-colors cursor-pointer hover:bg-muted/50 ${
                          isLatest
                            ? "border-primary/50 bg-primary/5"
                            : "border-border"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="text-2xl">
                              {session.accuracyRate === 100
                                ? "🎯"
                                : session.accuracyRate >= 80
                                ? "✨"
                                : session.accuracyRate >= 60
                                ? "💪"
                                : "📝"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">
                                  {session.accuracyRate.toFixed(0)}% accuracy
                                </span>

                                {/* Difficulty Badge (if not default) */}
                                {difficultyLabel && (
                                  <Badge
                                    variant={difficultyVariant}
                                    className="text-xs"
                                  >
                                    {difficultyLabel}
                                  </Badge>
                                )}

                                {/* Map Settings Badges */}
                                {mapSettings.map((setting) => (
                                  <Badge
                                    key={setting}
                                    variant="outline"
                                    className="text-xs"
                                  >
                                    {setting}
                                  </Badge>
                                ))}

                                {/* Anomalous Session Indicator */}
                                {isAnomalousSession(session) && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                    title="Session time excluded from aggregates (>1 min per question suggests AFK)"
                                  >
                                    ⏱️ Time excluded
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {new Date(
                                  session.completedAt
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}{" "}
                                •{" "}
                                {new Date(session.completedAt).toLocaleTimeString("en-US", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                })}
                                {isFirst && (
                                  <span className="ml-2 text-primary">
                                    (First attempt)
                                  </span>
                                )}
                                {isLatest && !isFirst && (
                                  <span className="ml-2 text-primary">
                                    (Most recent)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right text-sm text-muted-foreground flex-shrink-0 ml-4">
                            <div>
                              {session.correctAnswers}/{session.totalQuestions}{" "}
                              correct
                            </div>
                            <div className="text-xs">
                              {formatTime(session.totalTimeMs)}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </div>
            </CardContent>
          </Card>

          {/* Session Detail View Dialog */}
          {selectedSession && selectedQuiz && (
            <SessionDetailView
              session={selectedSession}
              config={selectedQuiz.config}
              isOpen={!!selectedSession}
              onClose={() => setSelectedSession(null)}
              previousSession={
                selectedQuiz.sessions.find(
                  (_s, i, arr) =>
                    arr.findIndex((x) => x.id === selectedSession.id) ===
                    i - 1
                )
              }
            />
          )}

          {/* Encouraging Message */}
          {selectedQuiz.daysSinceLastSession > 7 && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">
                  💡 It's been {selectedQuiz.daysSinceLastSession} days since
                  your last practice. A quick review helps keep your knowledge
                  fresh!
                </p>
                <Button
                  size="sm"
                  className="mt-3"
                  onClick={() =>
                    (window.location.href = `/quiz/play?signature=${selectedQuiz.config.signature}`)
                  }
                >
                  Practice Again
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Stat Card Component
// ============================================================================

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  trend?: string;
}

function StatCard({ title, value, subtitle, icon, trend }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        {trend && (
          <p className="text-xs text-green-600 dark:text-green-400 mt-2 font-medium">
            {trend}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Utilities
// ============================================================================

function formatTime(ms: number): string {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}
